import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EntityMgmtAppService } from './entity-mgmt-app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
/**
 * Diagnostics Maintenance is served by this component when mounted as the
 * workspace CE for `workspace.diagnostics-maintanence` (screen_key 10791).
 * The component still supports the standalone `/entity-mgmt` route for legacy
 * usage — workspace mode is detected at runtime from the active screen.
 */
type DiagFilterMenuKey = 'category-filter-menu' | 'subcat-filter-menu' | 'msg-filter-menu';

interface DiagFilterMenu {
  keys: Array<{ key: any; label: string; selected?: boolean; show?: boolean; hide?: boolean }>;
  propName: string;
  applied?: boolean;
}

@Component({
    selector: 'gtw-entity-mgmt-app',
    templateUrl: './entity-mgmt-app.component.html',
    styleUrls: ['./entity-mgmt-app.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EntityMgmtAppComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  private isLoading = false;

  baseURLs: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};
  headerData: any = {};
  USER_SETTINGS: any = {};

  // Workspace screen context
  currentScreenKey = '';
  currentScreenLink = '';
  private activeScreen: any = null;

  loadingList = false;
  loadingDetails = false;
  loadingDiag = false;
  errorMessage = '';
  entities: any[] = [];
  selectedEntity: any = null;
  entityDetails: any = null;
  entityDetailsEdit: any = null;
  enableEdit = false;
  savingDetails = false;
  addressSaving = false;
  ownershipSaving = false;
  addressList: any[] = [];
  ownershipList: any[] = [];
  addressListEdit: any[] = [];
  ownershipListEdit: any[] = [];
  activeTab: 'list' | 'details' | 'diagSummary' | 'diagMaint' = 'list';

  // Diagnostics Maintenance state
  diagList: any[] = [];
  diagListCopy: any[] = [];
  categories: any[] = [];
  subcategories: any[] = [];
  messages: any[] = [];
  allEntities: any[] = [];
  disabledEntities: any[] = [];
  selectedDiag: any = null;
  runningDiagnostics = false;
  togglingDiagKey: string | number | null = null;

  // Column filter menu state (mirrors legacy ctrl.filter)
  diagFilter: Record<DiagFilterMenuKey, DiagFilterMenu> = this.emptyFilter();
  filterSearchTerm = '';
  openFilterId: DiagFilterMenuKey | null = null;
  categorySelectAll = false;
  subcatSelectAll = false;
  msgSelectAll = false;

  // Column sort state. `diagSortColumn` holds the active row property name
  // (DIAG_KEY / DIAG_DESC / CATEGORY_DESC / SUBCATEGORY_DESC / DIAG_MSG);
  // `diagSortDirection` toggles asc/desc on repeated clicks. Reflected in
  // `diagListCopy` directly so all renders (post-filter, post-cache) honor it.
  diagSortColumn: string | null = null;
  diagSortDirection: 'asc' | 'desc' = 'asc';

  // Global table search (above the grid). Free-text, case-insensitive, ANDed
  // with the column-filter selections; both feed through `computeDiagFiltered`.
  diagSearchTerm = '';
  private static readonly DIAG_SEARCH_FIELDS = [
    'DIAG_KEY',
    'DIAG_DESC',
    'CATEGORY_DESC',
    'SUBCATEGORY_DESC',
    'DIAG_MSG',
  ] as const;

  // Edit-diagnostic modal state
  editModalOpen = false;
  editSelectedCat: any = null;
  editSelectedSubcat: any = null;
  editSelectedMsg: any = null;
  editDisabledLEs: any[] = [];
  editAllEntities: any[] = [];
  editDisabledSearchTerm = '';
  editEntitySearchTerm = '';
  editLoadingEntities = false;
  editLoadingLEs = false;
  editSaving = false;

  // Standalone-mode edit panel (kept for backward compatibility with the
  // non-workspace shell). Workspace CE uses the inline modal above.
  editDiagPayload: any = {
    cat_key: '',
    subcat_key: '',
    diag_msg: '',
    leid: '',
  };

  @Input('base-urls')
  set _base_urls(v: string) {
    try {
      const urls = safeJsonInput<any>(v || '{}', null, 'entity-mgmt-app.parse1');
      if (Object.keys(urls).length > 0) {
        this.baseURLs = urls;
        this.tryInit();
      }
    } catch {
      console.log('base-urls input not yet stable.');
    }
  }

  @Input('header-data')
  set _header_data(v: string) {
    try {
      const header = safeJsonInput<any>(v || '{}', null, 'entity-mgmt-app.parse2');
      if (Object.keys(header).length > 0) {
        this.globalService = header.globalParams || {};
        this.GENERAL_CONFIG = header.generalConfigData || {};
        this.USER_SETTINGS = header.userSettingsData || {};
        this.headerData = header;
        this.tryInit();
      }
    } catch {
      console.log('header-data input not yet stable.');
    }
  }

  @Input('row-data')
  set _row_data(v: string) {
    try {
      this.rowData = safeJsonInput<any>(v || '{}', null, 'entity-mgmt-app.parse3');
      this.tryInit();
    } catch {
      console.log('row-data input not yet stable.');
    }
  }

  @Input('filter-params')
  set _filter_params(v: string) {
    try {
      this.filterParams = safeJsonInput<any>(v || '{}', null, 'entity-mgmt-app.parse4');
      this.tryInit();
    } catch {
      console.log('filter-params input not yet stable.');
    }
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private service: EntityMgmtAppService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private snackbar: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    if (this.isDiagMaintWorkspaceMode) {
      this.activeTab = 'diagMaint';
    }
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        if (!this.isDiagMaintWorkspaceMode) return;
        // Restore tab cache if user revisits the screen — matches legacy
        // `vm.diagMaintCache = vm.activeScreenLocal.data || {}` behavior.
        this.restoreDiagMaintCache();
        if (!this.gtwWorkspace.dataFetched.value) {
          this.cdr.detectChanges();
          return;
        }
        if (this.isInitialized) {
          this.loadDiagMaintGrid();
        } else {
          this.tryInit();
        }
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        if (!this.isDiagMaintWorkspaceMode) return;
        if (!this.gtwWorkspace.dataFetched.value) {
          this.cdr.detectChanges();
          return;
        }
        if (this.isInitialized) {
          this.loadDiagMaintGrid();
        } else {
          this.tryInit();
        }
      })
    );

    if (this.isDiagMaintWorkspaceMode) {
      this.restoreDiagMaintCache();
    }
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.activeScreen = activeScreen;
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    // Workspace-derived context overrides any @Input values only when the
    // workspace actually owns the active screen. For standalone /entity-mgmt
    // usage (no active screen) we keep the @Input values.
    if (activeScreen && Object.keys(activeScreen).length > 0) {
      this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
      this.globalService = header.globalParams || {};
      this.GENERAL_CONFIG = header.generalConfigData || {};
      this.USER_SETTINGS = header.userSettingsData || {};
      this.headerData = header;
      this.filterParams = normalizeFilterParams(activeScreen);
      this.rowData = rowPayloadFromActiveScreen(activeScreen);
      this.currentScreenKey = String(activeScreen?.screen_key || activeScreen?.id || '');
      this.currentScreenLink = String(activeScreen?.link || '');
    }
    this.cdr.detectChanges();
  }

  get isDiagMaintWorkspaceMode(): boolean {
    if (this.currentScreenKey === '10791') return true;
    const link = (this.currentScreenLink || '').toLowerCase();
    // Match server spelling ("diagnostics-maintanence") and corrected spelling.
    return link.indexOf('diagnostics-mainten') >= 0;
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    if (this.isDiagMaintWorkspaceMode) {
      // Workspace CE: gate on Filter click + non-empty filter params, then
      // load the diagnostics grid (legacy `dataFilters:refreshGrid` → `getDiagList`).
      if (!this.gtwWorkspace.dataFetched.value) return;
      if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
      this.isInitialized = true;
      this.ensureEditPopupLists();
      this.loadDiagMaintGrid();
      return;
    }
    // Standalone / dialog usage retains the prior behavior.
    if (!this.filterParams || Object.keys(this.filterParams).length === 0) return;
    this.isInitialized = true;
    this.loadEntities();
  }

  private get ssoId(): string {
    return (
      this.USER_SETTINGS?.user?.sso_id ||
      this.headerData?.userSettingsData?.user?.sso_id ||
      this.globalService?.sso_id ||
      this.gtwGlobal.global?.globalParams?.sso_id ||
      ''
    );
  }

  private get taxYear(): string {
    return String(this.filterParams?.tax_year || this.globalService?.tax_year || '');
  }

  private get scenario(): string {
    return String(this.filterParams?.scenario || this.globalService?.scenario || '');
  }

  private get parentMeString(): string {
    return String(this.filterParams?.parent_me_string || this.filterParams?.me_string || '');
  }

  loadEntities(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.loadingList = true;
    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.detectChanges();
    this.service.loadEntityList(baseUrl, this.globalService, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        const data = res?.data?.DATA || res?.data?.data || res?.data || [];
        this.entities = Array.isArray(data) ? data : [];
        this.loadingList = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingList = false;
        this.isLoading = false;
        this.errorMessage = 'Unable to load entity list.';
        this.cdr.detectChanges();
      },
    });
  }

  selectEntity(row: any): void {
    this.selectedEntity = row;
    const leid = row?.LEID || row?.LEGAL_ENTITY_ID || row?.leid;
    if (!leid) return;
    const taxYear = this.filterParams?.tax_year || this.globalService?.tax_year || row?.TAX_YEAR || '';
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    if (!customApiUrl) return;
    this.loadingDetails = true;
    this.service.loadEntityDetails(customApiUrl, taxYear, leid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.entityDetails = res?.data || res || null;
        this.entityDetailsEdit = this.entityDetails ? safeJsonInput<any>(JSON.stringify(this.entityDetails), null, 'entity-mgmt-app.parse5') : null;
        this.addressList = this.entityDetails?.leAddressList || this.entityDetails?.addressList || [];
        this.ownershipList = this.entityDetails?.ownerShipList || this.entityDetails?.ownershipList || [];
        this.addressListEdit = safeJsonInput<any>(JSON.stringify(this.addressList || []), null, 'entity-mgmt-app.parse6');
        this.ownershipListEdit = safeJsonInput<any>(JSON.stringify(this.ownershipList || []), null, 'entity-mgmt-app.parse7');
        this.loadingDetails = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingDetails = false;
        this.entityDetails = null;
        this.cdr.detectChanges();
      },
    });
  }

  setTab(tab: 'list' | 'details' | 'diagSummary' | 'diagMaint'): void {
    this.activeTab = tab;
    if (tab === 'diagSummary') {
      this.loadDiagSummary();
    } else if (tab === 'diagMaint') {
      this.ensureEditPopupLists();
    }
    this.cdr.detectChanges();
  }

  loadDiagSummary(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.loadingDiag = true;
    this.diagList = [];
    this.service.getDiagList(baseUrl, this.taxYear, this.scenario, this.parentMeString, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.diagList = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.loadingDiag = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingDiag = false;
        this.errorMessage = 'Unable to load diagnostics.';
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Diagnostics Maintenance grid load (workspace CE path).
   * Mirrors legacy `DiagMaintCtrl.getDiagList`: pulls fresh filter values
   * from `workspaceFactory.activeScreen.filters` on every Filter click, hits
   * `action_code=zz9no5`, rebuilds the column filter menus and caches the
   * result on `activeScreen.data` so a tab revisit restores instantly.
   */
  loadDiagMaintGrid(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.loadingList = true;
    this.isLoading = true;
    this.errorMessage = '';
    this.diagListCopy = [];
    this.cdr.detectChanges();

    this.service.getDiagList(baseUrl, this.taxYear, this.scenario, this.parentMeString, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (res?.callSuccess === '1') {
          const list = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
          this.diagList = list;
          this.diagListCopy = list.slice();
          if (this.diagListCopy.length === 0) {
            this.snackbar.openSnackBar(
              'No data for the selected tax year and GOK, please try a different one.',
              'gtw-snackbar--orange'
            );
          } else {
            this.prepareFilterLists();
            this.applyCurrentSort();
          }
          this.cacheDiagMaint();
        } else {
          this.snackbar.openSnackBar('Error loading the grid, please try again!', 'gtw-snackbar--red');
        }
        this.loadingList = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loadingList = false;
        this.isLoading = false;
        this.snackbar.openSnackBar('Error loading the grid, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Lazy-load the edit modal's static lookups (category/subcategory/message).
   * Legacy `getEditPopupLists` runs once per tab session — these don't depend
   * on filter selections.
   */
  private ensureEditPopupLists(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    if (this.categories.length === 0) {
      this.service.getCategoryList(baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            this.categories = Array.isArray(res.jsonObject) ? res.jsonObject : [];
            this.cacheDiagMaint();
            this.cdr.detectChanges();
          }
        },
        error: () => this.snackbar.openSnackBar('Error loading the category list!', 'gtw-snackbar--red'),
      });
    }
    if (this.subcategories.length === 0) {
      this.service.getSubcategoryList(baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            this.subcategories = Array.isArray(res.jsonObject) ? res.jsonObject : [];
            this.cacheDiagMaint();
            this.cdr.detectChanges();
          }
        },
        error: () => this.snackbar.openSnackBar('Error loading the subcategory list!', 'gtw-snackbar--red'),
      });
    }
    if (this.messages.length === 0) {
      this.service.getMessageList(baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          if (res?.callSuccess === '1') {
            this.messages = Array.isArray(res.jsonObject) ? res.jsonObject : [];
            this.cacheDiagMaint();
            this.cdr.detectChanges();
          }
        },
        error: () => this.snackbar.openSnackBar('Error loading the message list!', 'gtw-snackbar--red'),
      });
    }
  }

  // ---------------- Diag Maint column filter helpers (legacy parity) ----------------

  private emptyFilter(): Record<DiagFilterMenuKey, DiagFilterMenu> {
    return {
      'category-filter-menu': { keys: [], propName: 'CAT_KEY' },
      'subcat-filter-menu': { keys: [], propName: 'SUBCAT_KEY' },
      'msg-filter-menu': { keys: [], propName: 'DIAG_MSG' },
    };
  }

  /**
   * Build category/subcategory/message keyed dropdowns from the freshly loaded
   * diag list, dropping duplicates by key. Mirrors legacy `prepareFilterLists`.
   */
  private prepareFilterLists(): void {
    const filter = this.emptyFilter();
    const seenCat: Record<string, boolean> = {};
    const seenSub: Record<string, boolean> = {};
    const seenMsg: Record<string, boolean> = {};

    for (const row of this.diagListCopy) {
      const cat = row.CAT_KEY;
      if (cat != null && !seenCat[cat]) {
        filter['category-filter-menu'].keys.push({ key: cat, label: row.CATEGORY_DESC });
        seenCat[cat] = true;
      }
      const sub = row.SUBCAT_KEY;
      if (sub != null && !seenSub[sub]) {
        filter['subcat-filter-menu'].keys.push({ key: sub, label: row.SUBCATEGORY_DESC });
        seenSub[sub] = true;
      }
      const msg = row.DIAG_MSG;
      if (msg != null && !seenMsg[msg]) {
        filter['msg-filter-menu'].keys.push({ key: msg, label: msg });
        seenMsg[msg] = true;
      }
    }
    this.diagFilter = filter;
  }

  filterKeys(menu: DiagFilterMenuKey): Array<{ key: any; label: string; selected?: boolean; show?: boolean; hide?: boolean }> {
    return this.diagFilter[menu]?.keys || [];
  }

  isFilterApplied(menu: DiagFilterMenuKey): boolean {
    return !!this.diagFilter[menu]?.applied;
  }

  openFilter(menu: DiagFilterMenuKey, event: MouseEvent): void {
    this.openFilterId = this.openFilterId === menu ? null : menu;
    this.filterSearchTerm = '';
    event.stopPropagation();
  }

  closeFilters(): void {
    this.openFilterId = null;
    this.filterSearchTerm = '';
  }

  /** Close any open column filter menu when the user clicks outside the table. */
  @HostListener('document:click', ['$event'])
  onDocClick(_event: Event): void {
    if (this.openFilterId) {
      this.closeFilters();
      this.cdr.detectChanges();
    }
  }

  /** Helper for the "Select All" checkbox in each column filter dropdown. */
  onFilterSelectAllChange(menu: DiagFilterMenuKey, checked: boolean): void {
    if (menu === 'category-filter-menu') this.categorySelectAll = checked;
    else if (menu === 'subcat-filter-menu') this.subcatSelectAll = checked;
    else this.msgSelectAll = checked;
    this.diagFilter[menu].keys.forEach((k) => {
      if (!k.hide && (k.show !== false)) {
        k.selected = checked;
      }
    });
  }

  /** Mirrors legacy `searchInFilter`: hides keys whose label doesn't match the search term. */
  isFilterKeyVisible(key: { selected?: boolean; show?: boolean; hide?: boolean; label: string }): boolean {
    if (key.selected) {
      key.show = true;
      return !key.hide;
    }
    const term = (this.filterSearchTerm || '').toLowerCase();
    if (term.length > 0) {
      key.show = (key.label || '').toLowerCase().includes(term);
    } else {
      key.show = true;
    }
    return !key.hide && key.show !== false;
  }

  onFilterKeySelectionChange(menu: DiagFilterMenuKey, key: any, checked: boolean): void {
    key.selected = checked;
    const allKeys = this.diagFilter[menu].keys;
    const allSelected = allKeys.length > 0 && allKeys.every((k) => k.selected);
    if (menu === 'category-filter-menu') this.categorySelectAll = allSelected;
    else if (menu === 'subcat-filter-menu') this.subcatSelectAll = allSelected;
    else this.msgSelectAll = allSelected;
  }

  /**
   * Chain all three column filters against the original diag list to produce
   * the visible `diagListCopy`. Empty selection on a column is treated as
   * "select all" (no constraint), matching legacy `applyFilters`. The global
   * `diagSearchTerm` is ANDed on top via `computeDiagFiltered`.
   */
  applyDiagFilters(menu: DiagFilterMenuKey): void {
    const cats = this.diagFilter['category-filter-menu'].keys;
    const subs = this.diagFilter['subcat-filter-menu'].keys;
    const msgs = this.diagFilter['msg-filter-menu'].keys;

    this.diagFilter['category-filter-menu'].applied = cats.some((k) => k.selected);
    this.diagFilter['subcat-filter-menu'].applied = subs.some((k) => k.selected);
    this.diagFilter['msg-filter-menu'].applied = msgs.some((k) => k.selected);

    this.computeDiagFiltered();
    this.refreshFilterKeyVisibility();
    this.applyCurrentSort();
    this.cacheDiagMaint();
    this.closeFilters();
    this.cdr.detectChanges();
    // Avoid the unused-parameter lint while keeping signature available for
    // future per-menu specialization (legacy passes the menu id).
    void menu;
  }

  /**
   * Free-text search above the table. Debouncing is unnecessary at this row
   * count (the legacy controller never debounced its in-memory filter), so
   * recompute synchronously on every keystroke.
   */
  onDiagSearchChange(term: string): void {
    this.diagSearchTerm = term ?? '';
    this.computeDiagFiltered();
    this.applyCurrentSort();
    this.cacheDiagMaint();
    this.cdr.detectChanges();
  }

  clearDiagSearch(): void {
    if (!this.diagSearchTerm) return;
    this.onDiagSearchChange('');
  }

  /**
   * Single source of truth that recomputes `diagListCopy` from `diagList` by
   * ANDing the active column-filter selections (empty selection == no
   * constraint, matching legacy `applyFilters`) with the global search term.
   */
  private computeDiagFiltered(): void {
    const cats = this.diagFilter['category-filter-menu'].keys;
    const subs = this.diagFilter['subcat-filter-menu'].keys;
    const msgs = this.diagFilter['msg-filter-menu'].keys;
    const selCats = cats.filter((k) => k.selected);
    const selSubs = subs.filter((k) => k.selected);
    const selMsgs = msgs.filter((k) => k.selected);

    const effCats = selCats.length === 0 ? cats : selCats;
    const effSubs = selSubs.length === 0 ? subs : selSubs;
    const effMsgs = selMsgs.length === 0 ? msgs : selMsgs;

    const needle = (this.diagSearchTerm || '').trim().toLowerCase();
    const fields = EntityMgmtAppComponent.DIAG_SEARCH_FIELDS;

    this.diagListCopy = this.diagList.filter((diag) => {
      if (
        !effCats.some((c) => c.key === diag.CAT_KEY) ||
        !effSubs.some((s) => s.key === diag.SUBCAT_KEY) ||
        !effMsgs.some((m) => m.key === diag.DIAG_MSG)
      ) {
        return false;
      }
      if (!needle) return true;
      for (let i = 0; i < fields.length; i++) {
        const v = diag?.[fields[i]];
        if (v !== null && v !== undefined && String(v).toLowerCase().indexOf(needle) !== -1) {
          return true;
        }
      }
      return false;
    });
  }

  // ---------------- Column sort ----------------

  /**
   * Toggle / set the active sort column for the diagnostics table.
   * Repeated clicks on the same header flip asc <-> desc; a click on a new
   * header resets to asc. `applyCurrentSort()` does the in-place sort so the
   * order persists across filter / cache rehydration.
   */
  sortDiagBy(column: string): void {
    if (!column) return;
    if (this.diagSortColumn === column) {
      this.diagSortDirection = this.diagSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.diagSortColumn = column;
      this.diagSortDirection = 'asc';
    }
    this.applyCurrentSort();
    this.cacheDiagMaint();
    this.cdr.detectChanges();
  }

  /**
   * Sort `diagListCopy` in-place by the active sort column / direction.
   * Strings compare via `localeCompare` (case-insensitive, numeric-aware);
   * everything else falls back to a numeric / loose comparison. Called on
   * every header click and after load / filter so the order is stable.
   */
  private applyCurrentSort(): void {
    if (!this.diagSortColumn) return;
    const col = this.diagSortColumn;
    const dir = this.diagSortDirection === 'asc' ? 1 : -1;
    this.diagListCopy = (this.diagListCopy || []).slice().sort((a, b) => {
      const av = a?.[col];
      const bv = b?.[col];
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === 'string' || typeof bv === 'string') {
        return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true }) * dir;
      }
      const an = Number(av);
      const bn = Number(bv);
      if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        return (an - bn) * dir;
      }
      return 0;
    });
  }

  /** Indicator helper for the template: returns 'asc' / 'desc' / '' for a column. */
  diagSortIndicator(column: string): 'asc' | 'desc' | '' {
    if (this.diagSortColumn !== column) return '';
    return this.diagSortDirection;
  }

  /** Hide keys from non-applied filter dropdowns that no longer match any visible row. */
  private refreshFilterKeyVisibility(): void {
    (Object.keys(this.diagFilter) as DiagFilterMenuKey[]).forEach((menu) => {
      const f = this.diagFilter[menu];
      if (f.applied) return;
      f.keys.forEach((k) => {
        k.hide = !this.diagListCopy.some((diag) => diag[f.propName] === k.key);
      });
    });
  }

  // ---------------- Diagnostics row actions ----------------

  /**
   * Toggle a diagnostic on/off. Legacy `toggleDiagStatus(diag, enabled)` sends
   * `diag_switch` as `1` / `0`; mirror that exact payload.
   */
  onToggleDiag(diag: any, enabled: number): void {
    if (!diag) return;
    const current = Number(diag.DIAG_SWITCH ?? 0);
    if (current === enabled) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    this.togglingDiagKey = diag.DIAG_KEY;
    const payload = {
      sso_id: this.ssoId,
      tax_year: this.taxYear,
      scenario: this.scenario,
      parent_me_string: this.parentMeString,
      diag_key: diag.DIAG_KEY,
      diag_switch: enabled,
    };
    this.service.toggleDiagStatus(baseUrl, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.togglingDiagKey = null;
        if (res?.callSuccess === '1') {
          diag.DIAG_SWITCH = enabled;
          this.cacheDiagMaint();
          this.snackbar.openSnackBar(
            `Diagnostic ${enabled ? 'Enabled' : 'Disabled'} Successfully!`,
            'gtw-snackbar--green'
          );
        } else {
          this.snackbar.openSnackBar('Something went wrong, please try again!', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.togglingDiagKey = null;
        this.snackbar.openSnackBar('Something went wrong, please try again!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  /** Open the edit-diagnostic modal (legacy `$uibModal` `edit-diag-modal.html`). */
  openEditDiag(diag: any): void {
    if (!diag) return;
    // Legacy: disabled diagnostics are not editable.
    if (!diag.DIAG_SWITCH) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;

    this.selectedDiag = diag;
    this.editSelectedCat = this.categories.find((c) => c.CAT_KEY === diag.CAT_KEY) || null;
    this.editSelectedSubcat = this.subcategories.find((s) => s.SUBCAT_KEY === diag.SUBCAT_KEY) || null;
    this.editSelectedMsg = this.messages.find((m) => m.DIAG_MSG === diag.DIAG_MSG) || null;
    this.editDisabledLEs = [];
    this.editAllEntities = [];
    this.editDisabledSearchTerm = '';
    this.editEntitySearchTerm = '';
    this.editLoadingEntities = true;
    this.editLoadingLEs = true;
    this.editModalOpen = true;
    this.cdr.detectChanges();

    this.service.getAllEntities(baseUrl, this.taxYear, this.scenario, this.parentMeString, diag.DIAG_KEY, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.editAllEntities = res?.callSuccess === '1' && Array.isArray(res.jsonObject) ? res.jsonObject : [];
        this.editAllEntities.forEach((e) => {
          e.selected = false;
          e.show = true;
        });
        this.editLoadingEntities = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.editLoadingEntities = false;
        this.snackbar.openSnackBar('Error loading the entity list!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });

    this.service.getDisabledLEs(baseUrl, this.taxYear, this.scenario, this.parentMeString, diag.DIAG_KEY, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.editDisabledLEs = res?.callSuccess === '1' && Array.isArray(res.jsonObject) ? res.jsonObject : [];
        this.editDisabledLEs.forEach((e) => {
          e.selected = false;
          e.show = true;
        });
        this.editLoadingLEs = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.editLoadingLEs = false;
        this.snackbar.openSnackBar('Error loading the disabled entities list!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  cancelEditDiag(): void {
    if (this.editSaving) return;
    this.editModalOpen = false;
    this.selectedDiag = null;
  }

  /** Edit modal: substring match (case-insensitive) across LEID and TAX_PAYER. */
  isEntityVisible(entity: any, term: string): boolean {
    if (!term) {
      entity.show = true;
      return true;
    }
    const t = term.toLowerCase();
    entity.show =
      String(entity.LEID || '').toLowerCase().includes(t) ||
      String(entity.TAX_PAYER || '').toLowerCase().includes(t);
    return entity.show;
  }

  selectAllDisabledInEdit(checked: boolean): void {
    this.editDisabledLEs.forEach((e) => {
      if (e.show !== false) e.selected = checked;
    });
  }

  selectAllEntitiesInEdit(checked: boolean): void {
    this.editAllEntities.forEach((e) => {
      if (e.show !== false) e.selected = checked;
    });
  }

  removeEntitiesFromDisabled(): void {
    const toMove = this.editDisabledLEs.filter((e) => e.selected);
    if (toMove.length === 0) return;
    this.editDisabledLEs = this.editDisabledLEs.filter((e) => !e.selected);
    toMove.forEach((e) => {
      e.selected = false;
      e.show = true;
    });
    this.editAllEntities = this.editAllEntities.concat(toMove).sort((a, b) =>
      String(a.LEID || '').localeCompare(String(b.LEID || ''))
    );
  }

  addEntitiesToDisabled(): void {
    const toMove = this.editAllEntities.filter((e) => e.selected);
    if (toMove.length === 0) return;
    this.editAllEntities = this.editAllEntities.filter((e) => !e.selected);
    toMove.forEach((e) => {
      e.selected = false;
      e.show = true;
    });
    this.editDisabledLEs = this.editDisabledLEs.concat(toMove).sort((a, b) =>
      String(a.LEID || '').localeCompare(String(b.LEID || ''))
    );
  }

  saveEditDiag(): void {
    if (this.editSaving || !this.selectedDiag) return;
    if (!this.editSelectedCat || !this.editSelectedSubcat || !this.editSelectedMsg) {
      this.snackbar.openSnackBar('Please select Category, Subcategory and Message.', 'gtw-snackbar--orange');
      return;
    }
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    const leIdStr = this.editDisabledLEs.map((d) => d.LEID).join(',');
    const payload = {
      tax_year: this.taxYear,
      scenario: this.scenario,
      parent_me_string: this.parentMeString,
      diag_key: this.selectedDiag.DIAG_KEY,
      cat_key: this.editSelectedCat.CAT_KEY,
      subcat_key: this.editSelectedSubcat.SUBCAT_KEY,
      diag_msg: this.editSelectedMsg.DIAG_MSG,
      leid: leIdStr,
      sso_id: this.ssoId,
    };
    this.editSaving = true;
    this.cdr.detectChanges();
    this.service.editDiagnostic(baseUrl, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.editSaving = false;
        if (res?.callSuccess === '1') {
          this.snackbar.openSnackBar('Diagnostic Details saved successfully!', 'gtw-snackbar--green');
          this.editModalOpen = false;
          this.selectedDiag = null;
          this.loadDiagMaintGrid();
        } else {
          this.snackbar.openSnackBar(
            'Something went wrong while saving your changes, please try again!',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.editSaving = false;
        this.snackbar.openSnackBar(
          'Something went wrong while saving your changes, please try again!',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Diagnostics Maintenance compatibility path for the legacy standalone
   * `diagMaint` edit panel (single-card form). Kept so the non-workspace
   * route still works without exposing the new modal.
   */
  saveEditedDiag(): void {
    if (!this.selectedDiag) return;
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl) return;
    const selectedDisabled = this.disabledEntities.map((d) => d.LEID).join(',');
    const payload = {
      tax_year: this.taxYear,
      scenario: this.scenario,
      parent_me_string: this.parentMeString,
      diag_key: this.selectedDiag?.DIAG_KEY,
      cat_key: this.editDiagPayload.cat_key,
      subcat_key: this.editDiagPayload.subcat_key,
      diag_msg: this.editDiagPayload.diag_msg,
      leid: selectedDisabled,
      sso_id: this.ssoId,
    };
    this.service.editDiagnostic(baseUrl, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.selectedDiag = null;
        this.loadDiagSummary();
      },
      error: () => {},
    });
  }

  runDiagnostics(): void {
    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.api;
    if (!baseUrl || this.runningDiagnostics) return;
    this.runningDiagnostics = true;
    this.cdr.detectChanges();
    this.service.runDiagnostics(baseUrl, {
      tax_year: this.taxYear,
      scenario: this.scenario,
      sso_id: this.ssoId,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.runningDiagnostics = false;
        if (res?.callSuccess === '1') {
          this.snackbar.openSnackBar('Job Initiation Successful!', 'gtw-snackbar--green');
        } else {
          this.snackbar.openSnackBar('Run Diagnostics Failed!', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.runningDiagnostics = false;
        this.snackbar.openSnackBar('Run Diagnostics Failed!', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }

  // ---------------- Active-screen cache ----------------

  /**
   * Persist the diag-maint state on `activeScreen.data` so a tab revisit
   * restores the loaded grid + filter selections without an extra request
   * (parity with legacy `workspaceFactory.setCache(activeScreenLocal, diagMaintCache)`).
   */
  private cacheDiagMaint(): void {
    if (!this.activeScreen) return;
    this.activeScreen.data = this.activeScreen.data || {};
    Object.assign(this.activeScreen.data, {
      diagList: this.diagList,
      diagListCopy: this.diagListCopy,
      filter: this.diagFilter,
      categories: this.categories,
      subcategories: this.subcategories,
      messages: this.messages,
      diagSortColumn: this.diagSortColumn,
      diagSortDirection: this.diagSortDirection,
      diagSearchTerm: this.diagSearchTerm,
    });
    this.activeScreen.cachedData = this.diagListCopy.length > 0;
  }

  private restoreDiagMaintCache(): void {
    const cache = this.activeScreen?.data;
    if (!cache) return;
    if (Array.isArray(cache.diagList)) this.diagList = cache.diagList;
    if (Array.isArray(cache.diagListCopy)) this.diagListCopy = cache.diagListCopy;
    if (cache.filter && typeof cache.filter === 'object') this.diagFilter = cache.filter;
    if (Array.isArray(cache.categories) && cache.categories.length > 0) this.categories = cache.categories;
    if (Array.isArray(cache.subcategories) && cache.subcategories.length > 0) this.subcategories = cache.subcategories;
    if (Array.isArray(cache.messages) && cache.messages.length > 0) this.messages = cache.messages;
    if (typeof cache.diagSortColumn === 'string' || cache.diagSortColumn === null) {
      this.diagSortColumn = cache.diagSortColumn;
    }
    if (cache.diagSortDirection === 'asc' || cache.diagSortDirection === 'desc') {
      this.diagSortDirection = cache.diagSortDirection;
    }
    if (typeof cache.diagSearchTerm === 'string') {
      this.diagSearchTerm = cache.diagSearchTerm;
    }
    if (this.diagListCopy.length > 0) {
      this.isInitialized = true;
    }
  }

  // ---------------- Legacy standalone helpers (entity-mgmt route only) ----------------

  selectAllEntitiesPanel(checked: boolean): void {
    this.allEntities.forEach((e) => (e.selected = checked));
  }

  selectAllDisabledPanel(checked: boolean): void {
    this.disabledEntities.forEach((e) => (e.selected = checked));
  }

  addEntitiesPanel(): void {
    const toAdd = this.allEntities.filter((e) => e.selected);
    if (toAdd.length === 0) return;
    const selectedSet = new Set(toAdd.map((e) => String(e.LEID)));
    toAdd.forEach((e) => (e.selected = false));
    this.allEntities = this.allEntities.filter((e) => !selectedSet.has(String(e.LEID)));
    this.disabledEntities = [...this.disabledEntities, ...toAdd].sort((a, b) =>
      String(a.LEID).localeCompare(String(b.LEID))
    );
    this.cdr.detectChanges();
  }

  removeEntitiesPanel(): void {
    const toRemove = this.disabledEntities.filter((e) => e.selected);
    if (toRemove.length === 0) return;
    const selectedSet = new Set(toRemove.map((e) => String(e.LEID)));
    toRemove.forEach((e) => (e.selected = false));
    this.disabledEntities = this.disabledEntities.filter((e) => !selectedSet.has(String(e.LEID)));
    this.allEntities = [...this.allEntities, ...toRemove].sort((a, b) =>
      String(a.LEID).localeCompare(String(b.LEID))
    );
    this.cdr.detectChanges();
  }

  toggleEdit(): void {
    this.enableEdit = !this.enableEdit;
    if (this.enableEdit) {
      this.entityDetailsEdit = this.entityDetails ? safeJsonInput<any>(JSON.stringify(this.entityDetails), null, 'entity-mgmt-app.parse8') : null;
      this.addressListEdit = safeJsonInput<any>(JSON.stringify(this.addressList || []), null, 'entity-mgmt-app.parse9');
      this.ownershipListEdit = safeJsonInput<any>(JSON.stringify(this.ownershipList || []), null, 'entity-mgmt-app.parse10');
    }
    this.cdr.detectChanges();
  }

  saveDetails(): void {
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    if (!customApiUrl || !this.entityDetailsEdit) return;
    this.savingDetails = true;
    this.service.saveEntityDetails(customApiUrl, this.entityDetailsEdit).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        if (res?.result?.value === '1') {
          this.entityDetails = safeJsonInput<any>(JSON.stringify(this.entityDetailsEdit), null, 'entity-mgmt-app.parse11');
          this.enableEdit = false;
          this.requestGridUpdate.emit('refresh');
        }
        this.savingDetails = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.savingDetails = false;
        this.cdr.detectChanges();
      },
    });
  }

  addAddressRow(): void {
    this.addressListEdit.push({});
    this.cdr.detectChanges();
  }

  removeAddressRow(index: number): void {
    const row = this.addressListEdit[index];
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    if (row?.reg_address_id && customApiUrl) {
      this.service.deleteAddressDetails(customApiUrl, this.taxYear, this.selectedEntity?.LEID || this.selectedEntity?.LEGAL_ENTITY_ID || '', row.reg_address_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.addressListEdit.splice(index, 1);
          this.cdr.detectChanges();
        },
        error: () => {},
      });
      return;
    }
    this.addressListEdit.splice(index, 1);
    this.cdr.detectChanges();
  }

  saveAddressDetails(): void {
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    const leid = this.selectedEntity?.LEID || this.selectedEntity?.LEGAL_ENTITY_ID || '';
    if (!customApiUrl || !leid) return;
    this.addressSaving = true;
    this.service.saveAddressDetails(customApiUrl, this.taxYear, leid, this.addressListEdit).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.service.reloadAddressDetails(customApiUrl, this.taxYear, leid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (reloadRes: any) => {
            const list = reloadRes?.data || reloadRes || [];
            this.addressList = Array.isArray(list) ? list : [];
            this.addressListEdit = safeJsonInput<any>(JSON.stringify(this.addressList), null, 'entity-mgmt-app.parse12');
            this.enableEdit = false;
            this.addressSaving = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.addressSaving = false;
            this.cdr.detectChanges();
          },
        });
      },
      error: () => {
        this.addressSaving = false;
        this.cdr.detectChanges();
      },
    });
  }

  addOwnershipRow(): void {
    this.ownershipListEdit.push({});
    this.cdr.detectChanges();
  }

  removeOwnershipRow(index: number): void {
    const row = this.ownershipListEdit[index];
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    const leid = this.selectedEntity?.LEID || this.selectedEntity?.LEGAL_ENTITY_ID || '';
    if (row?.ownership_id && customApiUrl && leid) {
      this.service.deleteOwnershipDetails(customApiUrl, leid, row.ownership_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.ownershipListEdit.splice(index, 1);
          this.cdr.detectChanges();
        },
        error: () => {},
      });
      return;
    }
    this.ownershipListEdit.splice(index, 1);
    this.cdr.detectChanges();
  }

  saveOwnershipDetails(): void {
    const customApiUrl = this.GENERAL_CONFIG?.custom_api_url || this.baseURLs?.custom;
    const leid = this.selectedEntity?.LEID || this.selectedEntity?.LEGAL_ENTITY_ID || '';
    if (!customApiUrl || !leid) return;
    this.ownershipSaving = true;
    this.service.saveOwnershipDetails(customApiUrl, this.taxYear, leid, this.ownershipListEdit).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.service.reloadOwnershipDetails(customApiUrl, this.taxYear, leid).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (reloadRes: any) => {
            const list = reloadRes?.data || reloadRes || [];
            this.ownershipList = Array.isArray(list) ? list : [];
            this.ownershipListEdit = safeJsonInput<any>(JSON.stringify(this.ownershipList), null, 'entity-mgmt-app.parse13');
            this.enableEdit = false;
            this.ownershipSaving = false;
            this.cdr.detectChanges();
          },
          error: () => {
            this.ownershipSaving = false;
            this.cdr.detectChanges();
          },
        });
      },
      error: () => {
        this.ownershipSaving = false;
        this.cdr.detectChanges();
      },
    });
  }

  get detailRows(): Array<{ key: string; value: any }> {
    const source = Array.isArray(this.entityDetails) ? this.entityDetails[0] : this.entityDetails;
    if (!source || typeof source !== 'object') return [];
    return Object.keys(source).map((k) => ({ key: k, value: source[k] }));
  }

  onRefresh(): void {
    if (this.isDiagMaintWorkspaceMode || this.activeTab === 'diagMaint') {
      this.loadDiagMaintGrid();
      this.requestGridUpdate.emit('refresh');
      return;
    }
    if (this.activeTab === 'list' || this.activeTab === 'details') {
      this.loadEntities();
    } else if (this.activeTab === 'diagSummary') {
      this.loadDiagSummary();
    }
    this.requestGridUpdate.emit('refresh');
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  trackByDiagKey(_index: number, row: any): any {
    return row?.DIAG_KEY ?? _index;
  }

  trackByLeId(_index: number, row: any): any {
    return row?.LEID ?? _index;
  }
}
